import {
    Dropdown,
    IContextualMenuItem,
    IDropdownOption,
    Icon,
    IconButton,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    Toggle,
    TooltipHost,
} from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { Banner } from '../../../Common/models/Banner';
import { dateLocaleStringWithTimeZone } from '../../../Common/util/DateUtils';
import { DateTimeBox } from '../../../HubLayout/components/Utilities/DateTimeBox';
import { Ellipsis } from '../../../HubLayout/components/Utilities/Ellipsis';

interface BannerCardProps {
    banner: Banner;
    index: number;
    isCollapsed: boolean;
    isDeleting: boolean;
    isNew: boolean;
    bannerTypeOptions: IDropdownOption[];
    theme: any;
    isMobile: boolean;
    onToggleCollapse: () => void;
    onSave: (banner: Banner) => void;
    onCancel: (bannerId: string) => void;
    getMenuItems: (banner: Banner) => IContextualMenuItem[];
}

export const BannerCard: React.FC<BannerCardProps> = ({
    banner: initialBanner,
    index,
    isCollapsed,
    isDeleting,
    isNew,
    bannerTypeOptions,
    theme,
    isMobile,
    onToggleCollapse,
    onSave,
    onCancel,
    getMenuItems,
}) => {
    const [banner, setBanner] = useState<Banner>(initialBanner);

    useEffect(() => {
        setBanner(initialBanner);
    }, [initialBanner]);

    const handleFieldChange = (field: keyof Banner, value: any) => {
        setBanner((prev) => ({ ...prev, [field]: value }));
    };

    const getBannerTypeIcon = (typeInt: number) => {
        switch (typeInt) {
            case 0:
                return 'Info';
            case 1:
                return 'Warning';
            case 2:
                return 'Error';
            default:
                return 'Info';
        }
    };

    const getBannerTypeColor = (typeInt: number) => {
        switch (typeInt) {
            case 0:
                return theme.palette.themePrimary;
            case 1:
                return theme.palette.orange;
            case 2:
                return theme.palette.redDark;
            default:
                return theme.palette.themePrimary;
        }
    };

    return (
        <div
            style={{
                padding: '15px',
                backgroundColor: theme.palette.neutralLighterAlt,
                borderTop: `1px solid ${theme.palette.neutralLight}`,
                borderRight: `1px solid ${theme.palette.neutralLight}`,
                borderBottom: `1px solid ${theme.palette.neutralLight}`,
                borderLeft: `4px solid ${
                    banner.isActive
                        ? theme.palette.green
                        : theme.palette.neutralTertiary
                }`,
                borderRadius: '4px',
                boxShadow:
                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: isCollapsed ? '0' : '16px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1,
                    }}
                >
                    {!isNew && (
                        <IconButton
                            iconProps={{
                                iconName: isCollapsed
                                    ? 'ChevronRight'
                                    : 'ChevronDown',
                            }}
                            title={isCollapsed ? 'Expand' : 'Collapse'}
                            ariaLabel={
                                isCollapsed
                                    ? 'Expand banner details'
                                    : 'Collapse banner details'
                            }
                            aria-expanded={!isCollapsed}
                            aria-controls={`banner-details-${index}`}
                            onClick={onToggleCollapse}
                            styles={{ root: { height: 32, width: 32 } }}
                        />
                    )}
                    <div style={{ flex: 1, marginRight: '12px' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Icon
                                iconName={getBannerTypeIcon(
                                    banner.typeInt ?? 0
                                )}
                                styles={{
                                    root: {
                                        fontSize: 20,
                                        color: getBannerTypeColor(
                                            banner.typeInt ?? 0
                                        ),
                                    },
                                }}
                            />
                            <Text
                                variant="large"
                                styles={{ root: { fontWeight: 600 } }}
                            >
                                {banner.title || `New Banner`}
                            </Text>
                            <TooltipHost
                                content={
                                    banner.isActive
                                        ? 'This banner is currently active'
                                        : 'This banner is inactive'
                                }
                            >
                                <Icon
                                    iconName={
                                        banner.isActive
                                            ? 'StatusCircleCheckmark'
                                            : 'StatusCircleRing'
                                    }
                                    styles={{
                                        root: {
                                            color: banner.isActive
                                                ? theme.palette.green
                                                : theme.palette
                                                      .neutralSecondary,
                                            fontSize: 16,
                                        },
                                    }}
                                />
                            </TooltipHost>
                        </div>
                        {isCollapsed && (
                            <>
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            display: 'block',
                                            marginTop: '4px',
                                        },
                                    }}
                                >
                                    {banner.message
                                        ? banner.message.length > 100
                                            ? `${banner.message.substring(
                                                  0,
                                                  100
                                              )}...`
                                            : banner.message
                                        : 'No message'}
                                </Text>
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            display: 'block',
                                            marginTop: '4px',
                                        },
                                    }}
                                >
                                    {banner.typeString} |{' '}
                                    {dateLocaleStringWithTimeZone(
                                        banner.startDate,
                                        'UTC'
                                    )}{' '}
                                    -{' '}
                                    {dateLocaleStringWithTimeZone(
                                        banner.endDate,
                                        'UTC'
                                    )}
                                </Text>
                            </>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {isDeleting && <Spinner size={SpinnerSize.small} />}
                    {!isNew && !isCollapsed && (
                        <Ellipsis menuItems={getMenuItems(banner)} />
                    )}
                </div>
            </div>

            <div
                id={`banner-details-${index}`}
                hidden={isCollapsed}
                style={{ paddingLeft: '0px' }}
            >
                {!isCollapsed && (
                    <Stack tokens={{ childrenGap: 16 }}>
                        {/* Title Field */}
                        <div
                            style={{
                                padding: '12px',
                                backgroundColor: theme.palette.neutralLighter,
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                            }}
                        >
                            <TextField
                                label="Title"
                                value={banner.title}
                                onChange={(_, newValue) =>
                                    handleFieldChange('title', newValue || '')
                                }
                                placeholder="Enter banner title"
                                required
                                borderless
                            />
                        </div>

                        {/* Message Field */}
                        <div
                            style={{
                                padding: '12px',
                                backgroundColor: theme.palette.neutralLighter,
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                            }}
                        >
                            <TextField
                                label="Message"
                                value={banner.message}
                                onChange={(_, newValue) =>
                                    handleFieldChange('message', newValue || '')
                                }
                                placeholder="Enter banner message"
                                multiline
                                autoAdjustHeight
                                required
                                borderless
                            />
                        </div>

                        {/* Configuration Section */}
                        <Stack
                            horizontal={!isMobile}
                            tokens={{ childrenGap: 12 }}
                        >
                            <Stack.Item grow={1}>
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderRadius: '4px',
                                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                    }}
                                >
                                    <Dropdown
                                        label="Banner Type"
                                        selectedKey={banner.typeInt ?? 0}
                                        options={bannerTypeOptions}
                                        onChange={(_, option) => {
                                            if (option) {
                                                handleFieldChange(
                                                    'type',
                                                    option.key as number
                                                );
                                                handleFieldChange(
                                                    'typeInt',
                                                    option.key as number
                                                );
                                                handleFieldChange(
                                                    'typeString',
                                                    option.text
                                                );
                                            }
                                        }}
                                        required
                                    />
                                </div>
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderRadius: '4px',
                                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: '60px',
                                    }}
                                >
                                    <Toggle
                                        label="Is Active"
                                        checked={banner.isActive}
                                        onChange={(_, isChecked) =>
                                            handleFieldChange(
                                                'isActive',
                                                isChecked || false
                                            )
                                        }
                                        onText="Active"
                                        offText="Inactive"
                                    />
                                </div>
                            </Stack.Item>
                        </Stack>

                        {/* Date Selection */}
                        <Stack
                            horizontal={!isMobile}
                            tokens={{ childrenGap: 12 }}
                        >
                            <Stack.Item grow={1}>
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderRadius: '4px',
                                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                    }}
                                >
                                    <DateTimeBox
                                        dateLabel="Start Date"
                                        timeLabel="Start Time (UTC)"
                                        setDate={(date) =>
                                            handleFieldChange('startDate', date)
                                        }
                                        value={banner.startDate}
                                    />
                                </div>
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderRadius: '4px',
                                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                    }}
                                >
                                    <DateTimeBox
                                        dateLabel="End Date"
                                        timeLabel="End Time (UTC)"
                                        setDate={(date) =>
                                            handleFieldChange('endDate', date)
                                        }
                                        value={banner.endDate}
                                    />
                                </div>
                            </Stack.Item>
                        </Stack>

                        {/* Date Preview */}
                        <div
                            style={{
                                padding: '12px',
                                backgroundColor: theme.palette.neutralLighter,
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                            }}
                        >
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        marginBottom: '4px',
                                        display: 'block',
                                    },
                                }}
                            >
                                Active Period
                            </Text>
                            <Text
                                variant="medium"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralPrimary,
                                    },
                                }}
                            >
                                {dateLocaleStringWithTimeZone(
                                    banner.startDate,
                                    'UTC'
                                )}{' '}
                                -{' '}
                                {dateLocaleStringWithTimeZone(
                                    banner.endDate,
                                    'UTC'
                                )}
                            </Text>
                        </div>

                        {!isNew && (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: theme.palette.white,
                                    borderRadius: '4px',
                                    marginBottom: '16px',
                                }}
                            >
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        <strong>Created By:</strong>{' '}
                                        {banner.createdBy} on{' '}
                                        {dateLocaleStringWithTimeZone(
                                            initialBanner.createdDate,
                                            'UTC'
                                        )}
                                    </Text>
                                    {initialBanner.lastUpdatedBy && (
                                        <Text
                                            variant="small"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .neutralSecondary,
                                                },
                                            }}
                                        >
                                            <strong>Last Updated By:</strong>{' '}
                                            {initialBanner.lastUpdatedBy} on{' '}
                                            {dateLocaleStringWithTimeZone(
                                                initialBanner.lastUpdatedDate!,
                                                'UTC'
                                            )}
                                        </Text>
                                    )}
                                </Stack>
                            </div>
                        )}

                        <Stack horizontal tokens={{ childrenGap: 12 }}>
                            <PrimaryButton
                                text={isNew ? 'Create' : 'Update'}
                                iconProps={{ iconName: 'Save' }}
                                onClick={() => onSave(banner)}
                                disabled={!banner.title || !banner.message}
                            />
                            <PrimaryButton
                                text="Cancel"
                                iconProps={{ iconName: 'Cancel' }}
                                onClick={() => onCancel(banner.id!)}
                                styles={{
                                    root: {
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderColor:
                                            theme.palette.neutralTertiary,
                                        color: theme.palette.neutralPrimary,
                                    },
                                    rootHovered: {
                                        backgroundColor:
                                            theme.palette.neutralLight,
                                        borderColor:
                                            theme.palette.neutralSecondary,
                                    },
                                }}
                            />
                        </Stack>
                    </Stack>
                )}
            </div>
        </div>
    );
};
